import { useEffect, useState } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import { HiClipboard } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  IconButton,
  Select,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { getAppDetails } from '../../redux/appBuilder/appDetails/selectors';
import { updateAppActiveStep } from '../../redux/appBuilder/stepperDetails';
import {
  getAppActiveStep,
  getAppStepperDetails,
} from '../../redux/appBuilder/stepperDetails/selectors';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getAppById } from '../../utils/app-builder/fetch-applications';
import { NewCodeRender } from './components/code-v2.component';

export const IntegrationDetails = () => {
  const navigate = useNavigate();
  const { appId } = useParams();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(getAppActiveStep);
  const appStepperDetails = useAppSelector(getAppStepperDetails);
  const [appData, setAppData] = useState<any>(useAppSelector(getAppDetails));
  const { width } = useWindowDimensions();

  // checkin if the state is coming from the view application or new application
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const toasts = useToast();
  useEffect(() => {
    if (!appData.appId) {
      const fetchData = async () => {
        const data = await getAppById(appId as string);
        if (data?.isSuccess) {
          setAppData(data?.application);
          setSelectedProviderId(data?.application.httpProvider[0].httpProviderId);
        } else {
          toast({
            title: 'Failed to fetch application',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      };

      fetchData();
    } else {
      if (appStepperDetails.appType !== 'ZK_FETCH') {
        setSelectedProviderId(appData.httpProvider[0].httpProviderId);
      }
    }
  }, [appData.appId]);

  const handleChange = (e: any) => {
    setSelectedProviderId(e.target.value);
  };

  const handleNext = () => {
    if (appStepperDetails.appVerified) {
      dispatch(updateAppActiveStep(5));
    } else {
      dispatch(updateAppActiveStep(4));
    }

    navigate('/new-application');
  };

  const handleAppIDCopyClick = () => {
    navigator.clipboard.writeText(appData?.appId);
    toasts({
      title: 'App ID Copied',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleAppSecretCopyClick = () => {
    if (appData?.privateKey) {
      navigator.clipboard.writeText(appData?.privateKey);
      toasts({
        title: 'App Secret Copied',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };
  console.log(appData);
  return (
    <Box display="flex" h="fit-content" justifyContent="center" width="100%">
      <Card
        w={width > 1740 ? '1300px' : '95%'}
        variant="outline"
        mt={6}
        backgroundColor="#FFF"
        borderRadius="12px"
        h="fit-content"
      >
        <CardBody p={0} justifyContent="space-between">
          <VStack
            paddingX="32px"
            paddingTop="32px"
            paddingBottom={activeStep === 3 ? '0px' : '32px'}
            h={activeStep === 3 ? '72vh' : '86vh'}
            overflowY="scroll"
          >
            <VStack align="flex-start" w="100%" spacing="2px">
              <Text
                fontFamily="inter"
                className="medium-font"
                fontWeight="bold"
                color="black"
              >
                Copy Paste Code
              </Text>
              <Text
                color="gray"
                className="small-font"
                whiteSpace="pre-line"
                fontFamily="inter"
              >
                Follow the Steps below to integrate in your application
              </Text>
            </VStack>
            <HStack flexWrap="wrap" mt="16px" w="100%" spacing="32px">
              <VStack alignItems="flex-start" w="35%" spacing={0}>
                <HStack>
                  <Text
                    color="gray"
                    className="small-font"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                    noOfLines={1}
                    fontFamily="inter"
                  >
                    Application ID
                  </Text>
                  <IconButton
                    size="sm"
                    icon={<HiClipboard />}
                    onClick={handleAppIDCopyClick}
                    aria-label="Copy App ID"
                  />
                </HStack>
                <Text
                  color="black.600"
                  className="small-font"
                  fontWeight="bold"
                  w="100%"
                  noOfLines={1}
                  whiteSpace="pre-line"
                  fontFamily="inter"
                >
                  {appData.appId}
                </Text>
              </VStack>
              <VStack alignItems="flex-start" w="35%" spacing="0px">
                <HStack>
                  <Text
                    color="gray"
                    className="small-font"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                    noOfLines={1}
                    fontFamily="inter"
                  >
                    Application Secret
                  </Text>
                  <IconButton
                    size="sm"
                    cursor={appData?.privateKey ? 'pointer' : 'not-allowed'}
                    icon={<HiClipboard />}
                    onClick={handleAppSecretCopyClick}
                    aria-label="Copy Secret Key"
                  />
                </HStack>
                <Text
                  color="black.600"
                  className="small-font"
                  w="100%"
                  whiteSpace="pre-line"
                  fontWeight="bold"
                  noOfLines={1}
                  fontFamily="inter"
                >
                  {appData?.privateKey || '**************************************'}
                </Text>
              </VStack>
              {(appData?.appType !== 'ZK_FETCH' ||
                appStepperDetails.appType !== 'ZK_FETCH') && (
                <VStack alignItems="flex-start" w="20%" spacing="0px">
                  <Text
                    color="gray"
                    className="small-font"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                    noOfLines={1}
                    fontFamily="inter"
                  >
                    Provider
                  </Text>
                  <Select
                    value={selectedProviderId}
                    // isReadOnly={true}
                    // pointerEvents='none'
                    onChange={handleChange}
                    opacity="1"
                    fontSize={width < 1368 ? '12px' : '14px'}
                    bg="gray.100"
                  >
                    {
                      // @ts-ignore
                      appData?.httpProvider &&
                        appData?.httpProvider.map((provider: any) => (
                          <option
                            key={provider.httpProviderId}
                            value={provider.httpProviderId}
                          >
                            {' '}
                            {provider.name}{' '}
                          </option>
                        ))
                    }
                  </Select>
                </VStack>
              )}
            </HStack>
            {appData?.privateKey && (
              <Flex
                bgColor="blue.50"
                px={5}
                mt="16px"
                w="100%"
                py={4}
                gap={3}
                borderRadius={8}
                borderLeft="4px solid"
                borderColor="blue.400"
                boxShadow="sm"
                alignItems="flex-start" // Add this line
              >
                <Box pt="3px" mt={1}>
                  <FiAlertCircle fontSize="1.3rem" color="blue.500" />
                </Box>
                <Flex direction="column">
                  <Heading
                    className="small-font"
                    fontFamily="inter"
                    fontWeight={600}
                    color="blue.700"
                  >
                    Make sure you copy the Application Secret
                  </Heading>
                  <Text className="small-font" fontFamily="inter" color="blue.600">
                    Once you leave this page, you will not be able to see the Application
                    Secret again
                  </Text>
                </Flex>
              </Flex>
            )}
            <VStack w="100%" mt="16px" spacing="0px">
              <VStack w="100%" align="flex-start" spacing="2px">
                <NewCodeRender
                  language={
                    appData?.appType
                      ? appData?.appType === 'MOBILE'
                        ? appData.appPlatform
                        : appData?.appType
                      : 'WEB'
                  }
                  selectionProviderId={selectedProviderId}
                  application={appData}
                />
              </VStack>
            </VStack>
          </VStack>
          {activeStep === 3 && (
            <VStack className="stepper-bar" alignItems="space-between">
              <Flex alignItems="center" gap={3} justifyContent="space-between">
                <Tooltip label="Edit App to Make Changes">
                  <Button
                    className="stepper-back-button small-font"
                    fontFamily="inter"
                    isDisabled
                  >
                    Back
                  </Button>
                </Tooltip>
                <Flex alignItems="center" gap="17px">
                  <Text color="gray.500" className="small-font">
                    {appStepperDetails.appType === 'ZK_FETCH' ? '2/3' : '3/4'}
                  </Text>
                  <Button
                    onClick={handleNext}
                    className="small-font stepper-next-button"
                    fontFamily="inter"
                    _hover={{
                      transform: 'scale(1.05)',
                    }}
                  >
                    Next
                  </Button>
                </Flex>
              </Flex>
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};
